import { useEffect, useState } from "react";

import { useBelegnummerPruefung } from "../../hooks/useInvoices";
import type { WebInvoiceDraft } from "../../lib/invoices";

type Props = {
  draft: WebInvoiceDraft;
  /** Die nächste freie Nummer laut Nummernkreis — der Weg zurück zur Automatik. */
  vorschlag: string;
  onChange: (draft: WebInvoiceDraft) => void;
  /**
   * Beleg ist schon festgeschrieben und wird gerade nur geändert.
   *
   * Dann ist die Nummer seine Identität — der Kunde hat sie womöglich schon.
   * Sie wird nicht nebenbei beim Textkorrigieren mitgeändert; dafür gibt es
   * „Belegnummer korrigieren" am Beleg, mit Kollisionsprüfung und Protokoll.
   */
  festgeschrieben?: boolean;
};

/**
 * Die Belegnummer im Editor: normalerweise gezogen, auf Wunsch von Hand.
 *
 * Warum es die Handvergabe gibt: solange Lexware und Kontor nebeneinander
 * laufen, ziehen zwei Programme aus getrennten Reihen. Kontor kennt nur sein
 * eigenes Register und schlägt deshalb gutgläubig eine Nummer vor, die drüben
 * längst beim Kunden liegt. Wer das weiß, muss es sagen können — vorher, nicht
 * hinterher.
 *
 * Der Normalfall bleibt unangetastet: Feld schreibgeschützt, Nummer entsteht
 * erst beim Festschreiben (atomar, damit abgebrochene Entwürfe keine Lücken
 * reißen). Erst der Klick auf „selbst vergeben" setzt `nummer_uebernehmen` —
 * dasselbe Flag, das ein KI-importierter Beleg trägt.
 *
 * Die Prüfung nebenan ist **Auskunft, keine Reservierung**. Zwischen Tippen und
 * Festschreiben kann sich das Register ändern; verbindlich entscheidet der
 * Server beim Festschreiben.
 */
export default function BelegNummerFeld({ draft, vorschlag, onChange, festgeschrieben }: Props) {
  const vonHand = Boolean(draft.nummer_uebernehmen) && !festgeschrieben;
  const [getippt, setGetippt] = useState(draft.doc_number);

  // Nicht bei jedem Tastenanschlag fragen. Ohne die Pause schickt eine
  // vierstellige Nummer vier Anfragen, von denen drei eine halbe Nummer prüfen
  // und „frei" melden — was schlimmer ist als keine Auskunft.
  useEffect(() => {
    const timer = setTimeout(() => setGetippt(draft.doc_number), 350);
    return () => clearTimeout(timer);
  }, [draft.doc_number]);

  const pruefung = useBelegnummerPruefung(getippt, draft.kind, vonHand);
  const befund = getippt === draft.doc_number ? pruefung.data : undefined;

  const handvergabeAn = () => onChange({ ...draft, nummer_uebernehmen: true });
  const zurueckZurAutomatik = () => onChange({
    ...draft,
    nummer_uebernehmen: false,
    doc_number: vorschlag || draft.doc_number,
  });

  return (
    <label className="invoice-editor-field invoice-nummer-feld">
      <span>
        {festgeschrieben ? "Nummer (vergeben)" : vonHand ? "Nummer (selbst vergeben)" : "Nummer"}
        {!festgeschrieben && (
          <button
            type="button"
            className="invoice-nummer-schalter"
            onClick={vonHand ? zurueckZurAutomatik : handvergabeAn}
          >
            {vonHand ? "automatisch vergeben" : "selbst vergeben"}
          </button>
        )}
      </span>

      {vonHand ? (
        <input
          value={draft.doc_number}
          onChange={(event) => onChange({ ...draft, doc_number: event.target.value })}
          placeholder={vorschlag ? `leer lassen = ${vorschlag}` : "leer lassen = nächste freie"}
          title="Diese Nummer beansprucht der Beleg beim Festschreiben"
          spellCheck={false}
          autoComplete="off"
        />
      ) : (
        <input
          value={draft.doc_number}
          readOnly
          title={festgeschrieben
            ? "Bereits vergeben — ändern über „Belegnummer korrigieren“ am Beleg"
            : "Wird beim Festschreiben atomar vergeben"}
        />
      )}
      {festgeschrieben && (
        <p className="invoice-nummer-befund">
          Bleibt beim Ändern gleich. Richtigstellen über „Belegnummer korrigieren“ am Beleg.
        </p>
      )}

      {vonHand && (
        <p className={`invoice-nummer-befund is-${befund ? (befund.frei ? "frei" : befund.art) : "unklar"}`}>
          {befund ? befund.hinweis : "Wird geprüft …"}
        </p>
      )}
    </label>
  );
}
